import React, { useEffect, useMemo, useRef, useState } from "react";
import './AvatarEditorBlock.css';
import './AvatarEditorBlockRange.css';

interface IAvatarEditorProps{
    originalImage: HTMLImageElement;
    previewCanvas: HTMLCanvasElement;
}

const createShadowPattern = ()=>{
    const size = 4;
    const canvas = document.createElement('canvas');
    canvas.width = size * 2;
    canvas.height = size * 2;
    const context = canvas.getContext('2d');
    context.fillStyle = '#0001';
    context.fillRect(0,0, size, size);
    context.fillRect(size, size, size, size);
    context.fillStyle = '#fff1';
    context.fillRect(0,size, size, size);
    context.fillRect(size, 0, size, size);
    return canvas;
}

export function AvatarEditorBlock({originalImage, previewCanvas}: IAvatarEditorProps){
    const [dragStart, setDragStart] = useState(false);
    const [position, setPosition] = useState({x: 0, y: 0});
    const [scale, setScale] = useState(0);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const imgParams = useMemo(()=>{
        if (!originalImage || !canvasRef.current || !position){
            return {};
        }
        const targetWidth = 250;
        const targetHeight = 250;
        const targetLeft = (canvasRef.current.width - targetWidth) / 2;
        const targetTop = (canvasRef.current.height - targetHeight) / 2;
    
        const img = originalImage;
        
        //const maxScaler = Math.max(canvasRef.current.width / img.naturalWidth, canvasRef.current.height / img.naturalHeight) *scale;
        const maxScaler = Math.max(targetWidth / img.naturalWidth, targetHeight / img.naturalHeight) * Math.exp(scale);
        console.log(scale, Math.exp(scale));
        const maxX = img.naturalWidth * maxScaler;
        const maxY = img.naturalHeight * maxScaler;
        const center = {
            x: (canvasRef.current.width - maxX) / 2,
            y: (canvasRef.current.height - maxY) / 2,
        } 
        return {targetHeight, targetWidth, targetLeft, targetTop, maxScaler, maxX, maxY, center};
    }, [originalImage, scale, position, canvasRef]);

    useEffect(()=>{
        if (!originalImage || !canvasRef.current || !position){
            return;
        }
        const context = canvasRef.current.getContext('2d');
        const img = originalImage;
        const {targetHeight, targetWidth, targetLeft, targetTop, maxScaler, maxX, maxY, center} = imgParams;
        const resultPosX = Math.max(Math.min(center.x + position.x, + targetLeft), canvasRef.current.width - maxX - targetLeft);
        const resultPosY = Math.max(Math.min(center.y + position.y, + targetTop), canvasRef.current.height - maxY - targetTop);
        context.fillStyle = '#999';
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        context.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, resultPosX, resultPosY, maxX , maxY);
        const shadowPattern = context.createPattern(createShadowPattern(), "repeat");
        //context.fillStyle = '#9999';
        context.fillStyle = shadowPattern;
        context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
       
        //const cropScaler = Math.max(targetWidth / img.naturalWidth, targetHeight / img.naturalHeight) * scale;
        //context.drawImage(img, -position.x, -position.y, maxX * canvasRef.current.width / targetWidth, maxY * canvasRef.current.height / targetHeight, targetLeft, targetTop, targetWidth, targetHeight);
        context.drawImage(img, -(resultPosX - targetLeft) / maxScaler, -(resultPosY - targetTop) /maxScaler, (targetWidth) / maxScaler , (targetHeight) / maxScaler, targetLeft, targetTop, targetWidth, targetHeight);
        context.beginPath();
        context.strokeStyle = '#fff';
        context.lineWidth = 1;
        context.rect(targetLeft-0.5, targetTop-0.5, targetWidth+1, targetHeight+1);
        context.closePath();
        context.stroke();
        
        context.beginPath();
        context.strokeStyle = '#000';
        context.lineWidth = 1;
        context.rect(targetLeft-1.5, targetTop-1.5, targetWidth+3, targetHeight+3);
        context.closePath();
        context.stroke();

    }, [originalImage, canvasRef.current, position, scale]);

    useEffect(()=>{
        if (!originalImage || !previewCanvas || !canvasRef.current || !position){
            return;
        }
        const context = previewCanvas.getContext('2d');
        const img = originalImage;
        const {targetHeight, targetWidth, targetLeft, targetTop, maxScaler, maxX, maxY, center} = imgParams;
        previewCanvas.width = targetWidth;
        previewCanvas.height = targetHeight;
        const resultPosX = Math.max(Math.min(center.x + position.x, + targetLeft), canvasRef.current.width - maxX - targetLeft);
        const resultPosY = Math.max(Math.min(center.y + position.y, + targetTop), canvasRef.current.height - maxY - targetTop);
        context.drawImage(img, -(resultPosX - targetLeft) / maxScaler, -(resultPosY - targetTop) /maxScaler, (targetWidth) / maxScaler , (targetHeight) / maxScaler, 0, 0, targetWidth, targetHeight);
    }, [originalImage, previewCanvas, position, scale])

    const handleCanvasMouseDown = ()=>{
        setDragStart(true);
        const handleMove = (e: MouseEvent)=>{
            console.log(e.movementX, e.movementY);
            const img = originalImage;
            
            const {targetLeft, targetTop, maxX, maxY, center} = imgParams;
            console.log(position);
            setPosition(last=> {
                //const resultPosX = last.x+ e.movementX + center.x;//Math.max(Math.min(center.x + last.x+ e.movementX, 0), canvasRef.current.width - maxX);
                //const resultPosY = last.y+ e.movementY + center.y;//Math.max(Math.min(center.y + last.y+ e.movementY, 0), canvasRef.current.height - maxY);
                const resultPosX = Math.max(Math.min(center.x + last.x + e.movementX, + targetLeft), canvasRef.current.width - maxX - targetLeft);
                const resultPosY = Math.max(Math.min(center.y + last.y + e.movementY, + targetTop), canvasRef.current.height - maxY - targetTop);
                return ({x: resultPosX - center.x , y: resultPosY - center.y })
            });
        }
        const handleUp = ()=>{
            setDragStart(false);
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        }
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    }

    const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>)=>{
        setScale(e.target.valueAsNumber);
        setPosition(last=> ({x: last.x / Math.exp(scale) * Math.exp(e.target.valueAsNumber), y: last.y / Math.exp(scale) * Math.exp(e.target.valueAsNumber)}))
    }

    const maxScaleValue = 3;
    return <div className="avatarEditor_wrapper">
        <div className="avatarEditor_canvas_area">
            <canvas 
                ref={canvasRef} 
                className="avatarEditor_canvas"
                width={400}
                height={300}
                onMouseDown={handleCanvasMouseDown}
                onDragStart={(e)=>e.preventDefault()}
                onSelect={(e)=>e.preventDefault()}
               
            ></canvas>
        </div>
        <div className="avatarEditor_form">
            <div className="avatarEditor_scaleBlock">
                <div>scale: </div>
                <input className="avatarEditor_scaleRange" type="range" value={scale} onChange={handleScaleChange} min={0} max={maxScaleValue} step={0.001} style={{background: `linear-gradient(to top, #fa0 ${scale / maxScaleValue * 100}%, #ccc ${scale / maxScaleValue * 100}%)`}}></input>
            </div>
        </div>
    </div>
}